import React, { useEffect, useMemo, useState } from 'react';
import {
  WarehouseStatCard, PickingQueue, LowStockAlerts, ActivityLog,
} from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import {
  WarehouseStatData, PickingQueueItem, LowStockItem, ActivityLogEntry,
} from './warehouseDashboardTypes';
import { getWarehouseDashboard, WarehouseDashboardData } from '../../../services/warehouseAnalyticsApi';
import styles from './WarehouseDashboard.module.css';

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${Math.max(m, 1)} minute${m === 1 ? '' : 's'} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'Yesterday';
  if (d < 7) return `${d} days ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const KIND_COLOR: Record<string, ActivityLogEntry['color']> = {
  po: 'green',
  transfer: 'purple',
  return: 'red',
  order: 'blue',
  adjustment: 'red',
};

const WarehouseDashboard: React.FC = () => {
  const [data, setData] = useState<WarehouseDashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true); setError(null);
    getWarehouseDashboard()
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const stats: WarehouseStatData[] = useMemo(() => {
    if (!data) return [];
    const s = data.stats;
    return [
      { id: 'orders-to-pick', title: 'Orders to Pick', value: String(s.orders_to_pick).padStart(2, '0'), color: 'blue', icon: 'ordersPick' },
      { id: 'orders-packed', title: 'Orders Packed Today', value: String(s.orders_packed_today).padStart(2, '0'), color: 'green', icon: 'ordersPacked' },
      { id: 'low-stock', title: 'Low Stock Items', value: String(s.low_stock_items).padStart(2, '0'), color: 'orange', icon: 'lowStock' },
      { id: 'damaged', title: 'Damaged Items', value: String(s.damaged_items).padStart(2, '0'), color: 'red', icon: 'damaged' },
      { id: 'returns', title: 'Pending Returns', value: String(s.pending_returns).padStart(2, '0'), color: 'purple', icon: 'returns' },
    ];
  }, [data]);

  const picking: PickingQueueItem[] = useMemo(() =>
    (data?.picking_queue ?? []).map((p) => ({
      id: String(p.order_id),
      orderId: p.order_number,
      itemCount: p.item_count,
      status: p.status === 'in_progress' ? 'In Progress' : 'Pending',
      priority: p.priority.toUpperCase() as PickingQueueItem['priority'],
    }))
  , [data]);

  const lowStock: LowStockItem[] = useMemo(() =>
    (data?.low_stock ?? []).map((l) => ({
      id: String(l.sku_id),
      productName: l.product_name,
      currentStock: l.current_stock,
      minStock: l.min_stock,
      severity: l.severity.toUpperCase() as LowStockItem['severity'],
    }))
  , [data]);

  const activity: ActivityLogEntry[] = useMemo(() =>
    (data?.activity ?? []).map((a) => ({
      id: a.id,
      title: a.title,
      description: a.description,
      timeAgo: relativeTime(a.occurred_at),
      author: a.author,
      color: KIND_COLOR[a.kind] ?? 'blue',
    }))
  , [data]);

  return (
    <div className={styles.container}>
      <AsyncBoundary
        loading={loading && !data}
        error={error}
        empty={false}
        onRetry={load}
      >
        <div className={styles.statsGrid}>
          {stats.map((stat) => (
            <WarehouseStatCard
              key={stat.id}
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              color={stat.color}
            />
          ))}
        </div>

        <div className={styles.middleRow}>
          <div className={styles.pickingCol}>
            <PickingQueue items={picking} />
          </div>
          <div className={styles.stockCol}>
            <LowStockAlerts items={lowStock} />
          </div>
        </div>

        <ActivityLog entries={activity} />
      </AsyncBoundary>
    </div>
  );
};

export const WarehouseDashboardPage: React.FC = () => {
  return <WarehouseDashboard />;
};

export default WarehouseDashboard;
